/*
 * BF BYOB builder — set + gift + upsell selection, pricing, cart add.
 * Pricing formulas identical to the PDP gift block (see bf-gift-block.js).
 * Gift goes to cart with the `_bf_gift: true` line-item property.
 */
(function () {
  const formatMoney = (cents) => {
    const locale = (window.Shopify?.locale || document.documentElement.lang || '').toLowerCase();
    if (locale.startsWith('fr') || locale.startsWith('de')) {
      return (cents / 100).toFixed(2).replace('.', ',') + '€';
    }
    const format = window.themeVariables?.settings?.moneyFormat;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(cents, format).trim();
    }
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  };

  class BFByob {
    constructor(root) {
      this.root = root;
      this.config = this._readConfig();
      this.upsellsContainer = root.querySelector('[data-bf-upsells]');
      this.dialog = root.querySelector('[data-bf-dialog]');
      this.delivery = root.querySelector('[data-bf-delivery]');

      // Teleport the floating ATC card to <body>: inside the section, an
      // ancestor with a transform/filter/containment (theme animations, wave
      // sections) turns position:fixed into position:absolute and lets
      // in-flow content and wave dividers paint over the bar. The element
      // carries its own bf-scheme class, so the --bf-* vars survive the
      // move. Because it leaves this.root, it gets its own click listener.
      this.float = root.querySelector('[data-bf-float]');
      if (this.float) {
        document.querySelectorAll('body > [data-bf-float]').forEach((el) => el.remove());
        document.body.appendChild(this.float);
        this.float.addEventListener('click', (event) => {
          const atc = event.target.closest('[data-bf-atc]');
          if (atc) this._addToCart(atc);
        });
      }
      this.carousels = {
        set: this._carousel('set'),
        gift: this._carousel('gift')
      };
      this._upsellCache = {};
      this._selectedUpsells = new Set();

      this._bind();
      this._bindExternalTriggers();
      this._initSwatchSliders();
      this._hideSingleColorBlocks();

      // Safety: if the pre-selected set didn't match any rendered card
      // (e.g. it went OOS), fall back to the first available card.
      if (!this._selectedSet()) {
        const firstSet = this.root.querySelector('[data-bf-set]');
        if (firstSet) firstSet.classList.add('is-selected');
      }
      if (!this._selectedGift()) {
        const firstGift = this.root.querySelector('[data-bf-gift]');
        firstGift?.classList.add('is-selected');
        const radio = firstGift?.querySelector('[data-bf-gift-radio]');
        if (radio) radio.checked = true;
      }

      this._avoidPreorderDefaults();
      this._syncAll();

      if (this.delivery) {
        setInterval(() => this._syncDelivery(), 30000);
      }
    }

    // A pre-order is never the default (client 2026-10-06). The swatch that
    // Liquid marks active is the product configured in the campaign, which
    // can be on pre-order. Move each card to its first in-stock colour, and
    // move the selected gift to the first visible gift that is in stock.
    // Pre-order stays the default only when nothing else is left.
    _avoidPreorderDefaults() {
      const isPreorder = (option) => option?.dataset.preorder === 'true';
      this.root.querySelectorAll('[data-bf-set], [data-bf-gift]').forEach((card) => {
        const active = this._activeOption(card);
        if (!isPreorder(active)) return;
        const inStock = card.querySelector('[data-bf-gift-option]:not([data-preorder="true"])');
        if (!inStock) return;
        active.classList.remove('is-active');
        inStock.classList.add('is-active');
      });

      const gift = this._selectedGift();
      if (gift && isPreorder(this._activeOption(gift))) {
        const better = Array.from(this.root.querySelectorAll('[data-bf-gift]')).find(
          (card) => !card.hidden && !isPreorder(this._activeOption(card))
        );
        if (better) {
          gift.classList.remove('is-selected');
          better.classList.add('is-selected');
          const radio = better.querySelector('[data-bf-gift-radio]');
          if (radio) radio.checked = true;
        }
      }
    }

    // A card with only one buyable colour left (after OOS/exclusion filtering
    // in Liquid) has nothing to pick — hide its "Kleur:" swatch row entirely.
    _hideSingleColorBlocks() {
      this.root.querySelectorAll('[data-bf-set], [data-bf-gift]').forEach((card) => {
        const swatches = card.querySelectorAll('[data-bf-gift-option]');
        if (swatches.length <= 1) {
          card.querySelector('.bf-set__colors, .bf-gift__colors')?.setAttribute('hidden', '');
        }
      });
    }

    _readConfig() {
      try {
        return JSON.parse(this.root.querySelector('[data-bf-byob-config]').textContent);
      } catch (error) {
        return {};
      }
    }

    /* ---------- selection helpers ---------- */

    _selectedSet() {
      return this.root.querySelector('[data-bf-set].is-selected');
    }

    _selectedGift() {
      return this.root.querySelector('[data-bf-gift].is-selected');
    }

    _activeOption(card) {
      if (!card) return null;
      return card.querySelector('[data-bf-gift-option].is-active') || card.querySelector('[data-bf-gift-option]');
    }

    /* ---------- events ---------- */

    _bind() {
      this.root.addEventListener('click', (event) => {
        const swatch = event.target.closest('[data-bf-gift-option]');
        if (swatch) {
          const card = swatch.closest('[data-bf-set], [data-bf-gift]');
          card.querySelectorAll('[data-bf-gift-option]').forEach((el) => el.classList.remove('is-active'));
          swatch.classList.add('is-active');
          this._selectCard(card);
          return;
        }

        const setHead = event.target.closest('[data-bf-set-head]');
        if (setHead) {
          this._selectCard(setHead.closest('[data-bf-set]'));
          return;
        }

        const more = event.target.closest('[data-bf-more]');
        if (more) {
          // data-bf-more-step = reveal in batches of N (sets, client
          // 2026-08-14); no attribute = reveal everything at once (gifts).
          const scope = more.dataset.bfMore === 'sets' ? '[data-bf-set]' : '[data-bf-gift]';
          const step = Number(more.dataset.bfMoreStep) || Infinity;
          const hiddenCards = Array.from(this.root.querySelectorAll(scope + '[hidden]'));
          hiddenCards.slice(0, step).forEach((card) => (card.hidden = false));
          if (hiddenCards.length <= step) more.hidden = true;
          return;
        }

        const expand = event.target.closest('[data-bf-expand]');
        if (expand) {
          this._openDialog(expand.dataset.bfExpand);
          return;
        }

        if (event.target.closest('[data-bf-dialog-prev]')) {
          this._dialogStep(-1);
          return;
        }
        if (event.target.closest('[data-bf-dialog-next]')) {
          this._dialogStep(1);
          return;
        }
        const dialogThumb = event.target.closest('[data-bf-dialog-thumb]');
        if (dialogThumb) {
          this._dialogIndex = Number(dialogThumb.dataset.bfDialogThumb) || 0;
          this._dialogRender();
          return;
        }

        if (event.target.closest('[data-bf-dialog-close]')) {
          this.dialog?.close();
          return;
        }

        const atc = event.target.closest('[data-bf-atc]');
        if (atc) {
          this._addToCart(atc);
        }
      });

      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-bf-gift-radio]')) {
          this._selectCard(event.target.closest('[data-bf-gift]'));
        }
        if (event.target.matches('[data-bf-upsell-checkbox]')) {
          if (event.target.checked) {
            this._selectedUpsells.add(event.target.value);
          } else {
            this._selectedUpsells.delete(event.target.value);
          }
          this._syncPricing();
        }
      });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      // Unlock the page when the popup closes (click-out, X or Esc).
      this.dialog?.addEventListener('close', () => {
        document.documentElement.classList.remove('bf-dialog-lock');
      });

      this._bindDialogSwipe();
    }

    /* Finger/mouse swipe on the popup image steps through the gallery. */
    _bindDialogSwipe() {
      const main = this.dialog?.querySelector('.bf-popup__main');
      if (!main) return;
      let startX = 0;
      let startY = 0;
      let tracking = false;
      main.addEventListener('pointerdown', (event) => {
        tracking = true;
        startX = event.clientX;
        startY = event.clientY;
      });
      main.addEventListener('pointerup', (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this._dialogStep(dx < 0 ? 1 : -1);
        }
      });
      main.addEventListener('pointercancel', () => {
        tracking = false;
      });
    }

    _selectCard(card) {
      if (!card) return;
      if (card.hasAttribute('data-bf-set')) {
        this.root.querySelectorAll('[data-bf-set]').forEach((el) => el.classList.toggle('is-selected', el === card));
        if (card.hidden) card.hidden = false;
        this._fetchUpsells();
      } else {
        this.root.querySelectorAll('[data-bf-gift]').forEach((el) => el.classList.toggle('is-selected', el === card));
        const radio = card.querySelector('[data-bf-gift-radio]');
        if (radio) radio.checked = true;
      }
      this._syncAll();
    }

    /* Entry point for OUTSIDE the builder (briefing, set-info segment):
       any element with data-bf-select-set="<productId>" selects that set —
       exact colour product — in the builder; the anchor href then scrolls
       up to #bf-byob. Used by bf-lander-product-tabs' primary CTA. */
    selectSetByProductId(productId) {
      if (!productId) return false;
      const option = this.root.querySelector(
        '[data-bf-set] [data-bf-gift-option][data-product-id="' + productId + '"]'
      );
      if (!option) return false;
      const card = option.closest('[data-bf-set]');
      card.querySelectorAll('[data-bf-gift-option]').forEach((el) => el.classList.remove('is-active'));
      option.classList.add('is-active');
      this._selectCard(card);
      return true;
    }

    _bindExternalTriggers() {
      document.addEventListener('click', (event) => {
        // Synthetic events (bots, in-app browsers) can target document or a text node, which have no closest().
        if (!(event.target instanceof Element)) return;
        const trigger = event.target.closest('[data-bf-select-set]');
        if (!trigger) return;
        this.selectSetByProductId(trigger.dataset.bfSelectSet);
        // no preventDefault — the anchor's default behavior scrolls to #bf-byob
      });
    }

    /* ---------- presentation sync ---------- */

    _syncAll() {
      this._syncSetCards();
      this._syncGiftCards();
      this._syncCarousel('set', this._activeOption(this._selectedSet()));
      this._syncCarousel('gift', this._activeOption(this._selectedGift()));
      this._syncBundle();
      this._syncPricing();
      this._syncDelivery();
      this._syncSwatchSliders();
    }

    /* ---------- swatch slider (PDP colour block pattern) ---------- */

    _initSwatchSliders() {
      // Swiper — the same mechanism as the PDP colour swatches
      // (product-info__variations-swiper): drag/touch + both arrows,
      // arrows auto-hide when everything fits (watchOverflow).
      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', () => this._initSwatchSliders(), { once: true });
        return;
      }
      this.root.querySelectorAll('[data-bf-swatches]:not([data-bf-swiper-init])').forEach((wrap) => {
        wrap.setAttribute('data-bf-swiper-init', 'true');
        new Swiper(wrap, {
          slidesPerView: 'auto',
          spaceBetween: 7,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          navigation: {
            nextEl: wrap.querySelector('[data-bf-swatches-next]'),
            prevEl: wrap.querySelector('[data-bf-swatches-prev]')
          }
        });
      });
      window.addEventListener('resize', () => this._syncSwatchSliders());
    }

    _syncSwatchSliders() {
      // Swiper keeps itself in sync via observer; nudge it after visibility
      // changes (show-more expand, set switch) just in case.
      this.root.querySelectorAll('[data-bf-swatches]').forEach((wrap) => wrap.swiper?.update());
    }

    _syncSetCards() {
      this.root.querySelectorAll('[data-bf-set]').forEach((card) => {
        const option = this._activeOption(card);
        if (!option) return;

        const price = Number(option.dataset.price) || 0;
        const compare = Math.max(Number(option.dataset.compare) || 0, price);
        const msrp = Number(option.dataset.msrp) || 0;

        const priceEl = card.querySelector('[data-bf-set-price]');
        if (priceEl) priceEl.textContent = formatMoney(price);
        const compareEl = card.querySelector('[data-bf-set-compare]');
        if (compareEl) {
          compareEl.textContent = formatMoney(compare);
          compareEl.hidden = compare <= price;
        }

        // White-badge chip — client 2026-08-09: the AMOUNT is always the LIVE
        // calculation (compare − price, whole euros, same integer math as
        // product-badges.liquid). msrp only picks the copy: msrp != price ->
        // korting text, msrp == price -> set deal text. No compare-at
        // savings -> no chip, exactly like live.
        const chip = card.querySelector('[data-bf-set-chip]');
        if (chip) {
          const symbol = this.config.currency_symbol || '€';
          // blank msrp counts as "not equal" -> korting copy (client
          // 2026-08-14, same rule as product-badges.liquid)
          if (compare > price) {
            const amount = symbol + Math.trunc((compare - price) / 100);
            const template = msrp !== price
              ? this.config.korting_template || '%discount%'
              : this.config.setdeal_template || '%discount%';
            chip.textContent = template.replace('%discount%', amount);
            chip.hidden = false;
          } else {
            chip.hidden = true;
          }
        }

        const colorName = card.querySelector('[data-bf-set-color-name]');
        if (colorName) colorName.textContent = option.dataset.colorName || '';

        // "+€50" delta against the primary colour of this card
        const delta = card.querySelector('[data-bf-set-color-delta]');
        if (delta) {
          const primary = card.querySelector('[data-bf-gift-option]');
          const deltaCents = price - (Number(primary?.dataset.price) || price);
          delta.textContent = deltaCents > 0 ? '+' + formatMoney(deltaCents) : '';
        }

        this._syncEta(card, option, '[data-bf-set-eta]');
      });
    }

    _syncGiftCards() {
      this.root.querySelectorAll('[data-bf-gift]').forEach((card) => {
        const option = this._activeOption(card);
        if (!option) return;

        const image = card.querySelector('[data-bf-gift-image]');
        if (image && option.dataset.image) image.src = option.dataset.image;
        const name = card.querySelector('[data-bf-gift-name]');
        if (name && option.dataset.title) name.textContent = option.dataset.title;
        const compare = card.querySelector('[data-bf-gift-compare]');
        if (compare) compare.textContent = formatMoney(Number(option.dataset.compare) || 0);
        const colorName = card.querySelector('[data-bf-gift-color-name]');
        if (colorName) colorName.textContent = option.dataset.colorName || '';

        this._syncEta(card, option, '[data-bf-gift-eta]');
      });
    }

    _syncEta(card, option, selector) {
      const eta = card.querySelector(selector);
      if (!eta) return;
      if (option.dataset.preorder === 'true' && option.dataset.etaDisplay) {
        const template = this.delivery?.dataset.preorderTemplate || '{date}';
        eta.textContent = template.replace('{date}', option.dataset.etaDisplay);
        eta.hidden = false;
      } else {
        eta.hidden = true;
      }
    }

    /* ---------- image carousel (photos of the active option) ---------- */

    _carousel(kind) {
      const rootEl = this.root.querySelector('[data-bf-carousel="' + kind + '"]');
      if (!rootEl) return null;
      const state = {
        root: rootEl,
        image: rootEl.querySelector('[data-bf-carousel-image]'),
        dots: rootEl.querySelector('[data-bf-carousel-dots]'),
        images: [],
        index: 0
      };
      rootEl.querySelector('[data-bf-carousel-prev]')?.addEventListener('click', () => this._carouselStep(state, -1));
      rootEl.querySelector('[data-bf-carousel-next]')?.addEventListener('click', () => this._carouselStep(state, 1));

      let touchStartX = null;
      rootEl.addEventListener('touchstart', (event) => (touchStartX = event.touches[0].clientX), { passive: true });
      rootEl.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const deltaX = event.changedTouches[0].clientX - touchStartX;
        if (Math.abs(deltaX) > 40) this._carouselStep(state, deltaX < 0 ? 1 : -1);
        touchStartX = null;
      }, { passive: true });

      return state;
    }

    _syncCarousel(kind, option) {
      const state = this.carousels[kind];
      if (!state || !option) return;
      state.images = (option.dataset.images || '').split('|').filter(Boolean);
      if (!state.images.length && option.dataset.image) state.images = [option.dataset.image];
      state.index = 0;
      state.alt = option.dataset.title || '';
      this._carouselRender(state);
    }

    _carouselStep(state, direction) {
      if (!state.images.length) return;
      state.index = (state.index + direction + state.images.length) % state.images.length;
      this._carouselRender(state);
    }

    _carouselRender(state) {
      if (state.image) {
        state.image.src = state.images[state.index] || '';
        state.image.alt = state.alt || '';
      }
      if (state.dots) {
        state.dots.innerHTML = '';
        state.images.forEach((_, index) => {
          const dot = document.createElement('span');
          dot.className = 'bf-byob__dot' + (index === state.index ? ' is-active' : '');
          state.dots.appendChild(dot);
        });
      }
    }

    /* ---------- upsells (complementary of the selected set) ---------- */

    async _fetchUpsells() {
      if (!this.upsellsContainer) return;
      const option = this._activeOption(this._selectedSet());
      if (!option) return;
      const productId = option.dataset.productId;
      const limit = Number(this.upsellsContainer.dataset.limit) || 4;

      this._selectedUpsells.clear();

      let itemsHtml = this._upsellCache[productId];
      if (itemsHtml == null) {
        try {
          // Section Rendering API instead of the JSON endpoint: the card
          // markup lives in sections/bf-upsell-items.liquid, which reads the
          // product.upsell_description metafield — the JSON API can't.
          const response = await fetch(
            `${Shopify.routes.root}recommendations/products?section_id=bf-upsell-items&product_id=${productId}&limit=${limit + 4}&intent=complementary`
          );
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          itemsHtml = Array.from(doc.querySelectorAll('.bf-upsell'))
            .slice(0, limit)
            .map((item) => item.outerHTML)
            .join('');
          this._upsellCache[productId] = itemsHtml;
        } catch (error) {
          itemsHtml = '';
        }
      }

      this.upsellsContainer.innerHTML = itemsHtml;

      this._syncPricing();
    }

    _upsellTotals() {
      let price = 0;
      let compare = 0;
      this.upsellsContainer?.querySelectorAll('[data-bf-upsell-checkbox]:checked').forEach((checkbox) => {
        price += Number(checkbox.dataset.price) || 0;
        compare += Number(checkbox.dataset.compare) || Number(checkbox.dataset.price) || 0;
      });
      return { price, compare };
    }

    /* ---------- bundle summary ---------- */

    _syncBundle() {
      const setOption = this._activeOption(this._selectedSet());
      const giftOption = this._activeOption(this._selectedGift());

      const setImage = this.root.querySelector('[data-bf-bundle-set-image]');
      if (setImage && setOption) setImage.src = setOption.dataset.image || '';
      const setName = this.root.querySelector('[data-bf-bundle-set-name]');
      if (setName && setOption) setName.textContent = setOption.dataset.title || '';
      const setColor = this.root.querySelector('[data-bf-bundle-set-color]');
      if (setColor && setOption) setColor.textContent = setOption.dataset.colorName || '';

      const giftImage = this.root.querySelector('[data-bf-bundle-gift-image]');
      if (giftImage && giftOption) giftImage.src = giftOption.dataset.image || '';
      const giftName = this.root.querySelector('[data-bf-bundle-gift-name]');
      if (giftName && giftOption) giftName.textContent = giftOption.dataset.title || '';

      const giftTwv = this.root.querySelector('[data-bf-bundle-gift-twv]');
      if (giftTwv && giftOption) {
        const compare = Number(giftOption.dataset.compare) || 0;
        giftTwv.textContent = compare > 0
          ? ' · ' + (giftTwv.dataset.template || '{price}').replace('{price}', formatMoney(compare))
          : '';
      }
    }

    /* ---------- pricing (formulas identical to the PDP gift block) ---------- */

    _syncPricing() {
      const setOption = this._activeOption(this._selectedSet());
      const giftOption = this._activeOption(this._selectedGift());
      if (!setOption) return;

      const corePrice = Number(setOption.dataset.price) || 0;
      const coreCompare = Math.max(Number(setOption.dataset.compare) || 0, corePrice);
      const coreMsrp = Number(setOption.dataset.msrp) || 0;
      const coreIsSet = setOption.dataset.isSet === 'true';
      const giftCompare = giftOption ? Number(giftOption.dataset.compare) || 0 : 0;
      const upsells = this._upsellTotals();

      const setValue = coreCompare + giftCompare + upsells.compare;
      // MSRP rule (client-confirmed): compare − MSRP when positive, no set_single check
      const bundleSavings = coreMsrp > 0 ? Math.max(0, coreCompare - coreMsrp) : 0;
      const total = corePrice + upsells.price;
      const bfDiscount = setValue - bundleSavings - total;
      const savings = setValue - total;
      // Floor, never round up — the shown % must not overstate the discount
      const savingsPct = setValue > 0 ? Math.floor((savings / setValue) * 100) : 0;

      // "Waarde van je set" only fits actual sets — singles get their own label
      const setValueLabel = this.root.querySelector('[data-bf-pricing-label="set_value"]');
      if (setValueLabel) {
        const label = coreIsSet ? setValueLabel.dataset.labelSet : setValueLabel.dataset.labelSingle;
        if (label) setValueLabel.textContent = label;
      }

      this._setRow('set_value', setValue, formatMoney(setValue));
      this._setRow('bundle_savings', bundleSavings, '- ' + formatMoney(bundleSavings));
      this._setRow('bf_discount', bfDiscount, '- ' + formatMoney(bfDiscount));
      this._setRow('total', total, formatMoney(total));
      this._setRow('savings', savings, formatMoney(savings));
      // Intelligems price-simple variant: Bundelvoordeel + BF-korting as one row, Je voordeel as % only
      this._setRow('discount_all', savings, '- ' + formatMoney(savings));
      this._setRow('savings_pct', savings, '');
      this.root.querySelectorAll('[data-bf-pricing-pct]').forEach((pctEl) => {
        pctEl.textContent = '-' + savingsPct + '%';
      });

      // Floating ATC card (PDP sticky-bar design): selected set image + name,
      // crossed deal value, bundle total, savings pill.
      const float = this.float;
      if (float) {
        const image = float.querySelector('[data-bf-float-image]');
        if (image) {
          if (setOption.dataset.image) image.src = setOption.dataset.image;
          image.alt = setOption.dataset.title || '';
        }
        const title = float.querySelector('[data-bf-float-title]');
        if (title) title.textContent = setOption.dataset.title || '';
        const compareEl = float.querySelector('[data-bf-float-compare]');
        if (compareEl) {
          compareEl.textContent = formatMoney(setValue);
          compareEl.hidden = setValue <= total;
        }
        const priceEl = float.querySelector('[data-bf-float-price]');
        if (priceEl) priceEl.textContent = formatMoney(total);
        const saveEl = float.querySelector('[data-bf-float-save]');
        if (saveEl) {
          saveEl.textContent = (saveEl.dataset.template || '{amount}').replace('{amount}', formatMoney(savings));
          saveEl.hidden = savings <= 0;
        }
      }



      const incl = this.root.querySelector('[data-bf-incl]');
      if (incl) {
        const inclText = incl.querySelector('[data-bf-incl-text]') || incl;
        if (giftOption) {
          const raw = (incl.dataset.template || '')
            .replace('{name}', giftOption.dataset.title || '')
            .replace('{price}', formatMoney(giftCompare));
          // escape, then resolve [[highlight]] markers
          const escaped = raw.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          inclText.innerHTML = escaped.replace(/\[\[(.+?)\]\]/g, '<b class="bf-incl-hl">$1</b>');
          incl.hidden = false;
        } else {
          incl.hidden = true;
        }
      }
    }

    _setRow(key, cents, formatted) {
      const row = this.root.querySelector('[data-bf-pricing-row="' + key + '"]');
      if (!row) return;
      row.hidden = cents <= 0;
      const value = row.querySelector('[data-bf-pricing-value="' + key + '"]');
      if (value) value.textContent = formatted;
    }

    /* ---------- delivery line ---------- */

    _syncDelivery() {
      if (!this.delivery) return;
      const setOption = this._activeOption(this._selectedSet());
      const giftOption = this._activeOption(this._selectedGift());

      const setIso = setOption?.dataset.preorder === 'true' ? setOption.dataset.etaIso || '' : '';
      const giftIso = giftOption?.dataset.preorder === 'true' ? giftOption.dataset.etaIso || '' : '';

      if (setIso || giftIso) {
        const display = giftIso > setIso ? giftOption.dataset.etaDisplay : setOption?.dataset.etaDisplay || giftOption?.dataset.etaDisplay;
        this.delivery.textContent = (this.delivery.dataset.preorderTemplate || '{date}').replace('{date}', display || '');
        this.delivery.classList.add('bf-byob__delivery--preorder');
        this._mirrorDelivery();
        return;
      }

      const [cutoffHours, cutoffMinutes] = (this.delivery.dataset.cutoff || '23:59').split(':').map((part) => parseInt(part, 10) || 0);
      const now = new Date();
      const cutoff = new Date(now);
      cutoff.setHours(cutoffHours, cutoffMinutes, 0, 0);
      let extraDays = 0;
      if (cutoff.getTime() <= now.getTime()) {
        cutoff.setDate(cutoff.getDate() + 1);
        extraDays = 1;
      }
      const diff = cutoff.getTime() - now.getTime();
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const time = (hours > 0 ? hours + 'u ' : '') + minutes + 'm';

      const deliveryDate = new Date(now);
      deliveryDate.setDate(deliveryDate.getDate() + (Number(this.delivery.dataset.days) || 1) + extraDays);
      // No deliveries on Sunday (ClickUp 1241uyj165f): a Saturday order arrives on Monday.
      if (deliveryDate.getDay() === 0) deliveryDate.setDate(deliveryDate.getDate() + 1);
      const locale = this.delivery.dataset.locale || 'nl';
      const date = deliveryDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

      this.delivery.textContent = (this.delivery.dataset.template || '').replace('{time}', time).replace('{date}', date);
      this.delivery.classList.remove('bf-byob__delivery--preorder');
      this._mirrorDelivery();
    }

    // The floating ATC card shows the same delivery/ETA line as the builder —
    // one source of truth: whatever _syncDelivery just wrote.
    _mirrorDelivery() {
      const float = this.float ? this.float.querySelector('[data-bf-float-delivery]') : null;
      if (!float) return;
      float.textContent = this.delivery.textContent;
      float.classList.toggle('bf-byob__delivery--preorder', this.delivery.classList.contains('bf-byob__delivery--preorder'));
    }

    /* ---------- dialog ---------- */

    _openDialog(kind) {
      if (!this.dialog) return;
      const card = kind === 'set' ? this._selectedSet() : this._selectedGift();
      const option = this._activeOption(card);
      if (!option) return;

      this._dialogImages = (option.dataset.images || '').split('|').filter(Boolean);
      if (!this._dialogImages.length && option.dataset.image) this._dialogImages = [option.dataset.image];
      if (!this._dialogImages.length) return;

      this._dialogAlt = option.dataset.title || '';
      // Open on the photo currently shown in the carousel (same image list,
      // same order), not always on the first one.
      const carouselIndex = this.carousels?.[kind]?.index || 0;
      this._dialogIndex = carouselIndex < this._dialogImages.length ? carouselIndex : 0;
      this._dialogRender();

      // Same guard as bf-gift-block.js: opening the modal must never move
      // the page — pin the scroll position through the open call.
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      this.dialog.showModal();
      window.scrollTo(scrollX, scrollY);
      document.documentElement.classList.add('bf-dialog-lock');
      requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
    }

    _dialogStep(direction) {
      if (!this._dialogImages?.length) return;
      this._dialogIndex = (this._dialogIndex + direction + this._dialogImages.length) % this._dialogImages.length;
      this._dialogRender();
    }

    _dialogRender() {
      const main = this.dialog.querySelector('[data-bf-dialog-main]');
      if (main) {
        main.src = this._dialogImages[this._dialogIndex] || '';
        main.alt = this._dialogAlt || '';
      }

      const thumbs = this.dialog.querySelector('[data-bf-dialog-thumbs]');
      if (thumbs) {
        thumbs.innerHTML = '';
        this._dialogImages.forEach((url, index) => {
          const thumb = document.createElement('button');
          thumb.type = 'button';
          thumb.className = 'bf-popup__thumb' + (index === this._dialogIndex ? ' is-active' : '');
          thumb.dataset.bfDialogThumb = index;
          thumb.innerHTML = `<img src="${url}" alt="" loading="lazy">`;
          thumbs.appendChild(thumb);
        });
      }
    }

    /* ---------- floating button ---------- */

    /* ---------- add to cart ---------- */

    async _addToCart(button) {
      const setOption = this._activeOption(this._selectedSet());
      const giftOption = this._activeOption(this._selectedGift());
      if (!setOption) return;

      const items = [];

      // Same VISIBLE property as the PDP buy-buttons emit (key = date label,
      // value with the leading space) — identical props make identical
      // variants merge into one cart line and show the date in the cart.
      const preorderLabel = this.config.preorder_property_label || 'Verwachte leverdatum +/-';

      const setItem = { id: Number(setOption.dataset.variantId), quantity: 1, properties: {} };
      if (setOption.dataset.preorder === 'true') {
        if (setOption.dataset.predikoId) setItem.properties._prediko_order_id = setOption.dataset.predikoId;
        if (setOption.dataset.etaIso) setItem.properties._prediko_date_iso = setOption.dataset.etaIso;
        if (setOption.dataset.etaDisplay) setItem.properties[preorderLabel] = ' ' + setOption.dataset.etaDisplay;
      }
      // pair markers for the BF gift discount function (property-based);
      // _bf_role carries the role through SupaEasy's single attribute slot
      if (giftOption) {
        setItem.properties._bf_core = 'true';
        setItem.properties._bf_role = 'core';
      }
      items.push(setItem);

      this.upsellsContainer?.querySelectorAll('[data-bf-upsell-checkbox]:checked').forEach((checkbox) => {
        items.push({ id: Number(checkbox.value), quantity: 1 });
      });

      if (giftOption) {
        // _bf_gift_key: unique per ATC run so identical gift lines never merge
        // — the cart transform zeroes whole lines only, and a merged qty-2
        // gift line turns fully paid when one core is removed (see theme.js
        // _getBfGiftItems for the same rule on the PDP path).
        const giftProperties = {
          _bf_gift: 'true',
          _bf_role: 'gift',
          _bf_gift_key: Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
        };
        if (giftOption.dataset.preorder === 'true') {
          if (giftOption.dataset.predikoId) giftProperties._prediko_order_id = giftOption.dataset.predikoId;
          if (giftOption.dataset.etaIso) giftProperties._prediko_date_iso = giftOption.dataset.etaIso;
          if (giftOption.dataset.etaDisplay) giftProperties[preorderLabel] = ' ' + giftOption.dataset.etaDisplay;
        }
        items.push({ id: Number(giftOption.dataset.variantId), quantity: 1, properties: giftProperties });
      }

      button.setAttribute('aria-busy', 'true');
      const errorEl = this.root.querySelector('[data-bf-error]');
      if (errorEl) errorEl.hidden = true;

      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: sectionsToBundle.join(',') })
        });
        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.description || 'Something went wrong');
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        document.documentElement.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: responseJson.items || [responseJson], cart: cartContent }
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent }
          })
        );
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-bf-byob]').forEach((root) => {
      if (!root.dataset.bfByobReady) {
        root.dataset.bfByobReady = 'true';
        const instance = new BFByob(root);
        instance._fetchUpsells();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
